import React, {
    useContext, useState, useEffect, useCallback, useRef
} from 'react';
import _ from 'lodash';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import appConfig from 'app-config';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';    
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import PropTypes from 'prop-types';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { AccountService } from 'gw-capability-gateway-policy';
import { EmailValidationService } from 'gw-capability-quoteandbind';
import { MLAddressDetailsComponent, MLNameDetailsComponent, MLPolicyNamedInsuredCandidatesComponent } from 'ml-components-common-react';
import metadata from './AdditionalInterestInputComponent.metadata.json5';
import messages from './AdditionalInterestInputComponent.messages';
import styles from './AdditionalInterestInputComponent.module.scss';

const contactSelectionApplicableTypes = [
    'FIRSTMORTGAGEE',
    'SECONDMORTGAGEE',
    'THIRDMORTGAGEE',
    'LIEN',
    'LEASINGCOMPANY',
    'LESSOR'
];
const thirdPartyTypes = [
    'thirdparty_ml',
    'THRIDPARTYNOTIFICATION_ML'
];
const leinOrLeasingCompanyTypes = [
    'LIEN',
    'LEASINGCOMPANY'
];
const modernProductInterestTypes = [
    'LESSOR',
    'additional_registered_owner_np',
    'certificate_of_insurance_np',
    'additonal_vehicle_insured_np',
    'LIEN',
    'TRUST_ML',
    'thirdparty_ml'
];
const modernProductThirdPartyStates = ['AK', 'CA', 'NJ', 'NY', 'CT'];

function AdditionalInterestInputComponent(props) {
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        addtlInterestVM: additionalInterestVM,
        submissionVM,
        endorsementVM,
        id,
        index,
        removeAdditionalInterest,
        path,
        onValueChange,
        updateViewModel,
        getSelectedInterestTypes,
        initialPotentialContact,
        showValidationErrors,
        onValidate,
        displayInterestedPartyTypeSelection,
        display,
        currentStepName,
        coveragePartTypeML,
        subtype,
        additionalInterestAvailableCandidatesML,
        defaultInterestPartytype
    } = props;
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const { refreshData } = useDataRefresh();

    const OTHER = 'other';
    const SELECT = 'select';
    const PERSON = 'Person';
    const COMPANY = 'Company';
    const CONTACT_SUBTYPE_COMPANY = 'Company';
    const FIRST_MORTGAGEE = 'FIRSTMORTGAGEE';
    const SECOND_MORTGAGEE = 'SECONDMORTGAGEE';
    const THIRD_MORTGAGEE = 'THIRDMORTGAGEE';

    const defaultToSelectBusinessContactByTypes = [
        'TRUST_ML'
    ];

    const isQnB = appConfig.portalCode === 'QB';
    const isHome = MLProductCodeUtil.isHomeOwnersPartialTypeHome(coveragePartTypeML);
    const isRenters = MLProductCodeUtil.isHomeOwnersPartialTypeRental(coveragePartTypeML);
    const isCondo = MLProductCodeUtil.isHomeOwnersPartialTypeCondo(coveragePartTypeML);
    const isLLHOrLLC = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(coveragePartTypeML)
        || MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(coveragePartTypeML);
    const isHomeCondoRentersLL = isHome || isRenters || isCondo || isLLHOrLLC;
    const isHomeCondo = isHome || isCondo
    const additionalInterestTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('AdditionalInterestType');
    const [showContactDetails, setShowContactDetails] = useState(false);
        const [interestedPartyTypeCodes, updateInterestedPartyTypeCodes] = useState([]);
        const [potentialContactsDropdown, updatePotentialContactsDropdown] = useState([]);
    const [availablePotentialContacts, updateAvailablePotentialContacts] = useState([]);
    const [interestOriginalCopy] = useState(_.cloneDeep(additionalInterestVM.value));
    const [prevInterestType, updatePrevInterestType] = useState(_.get(additionalInterestVM, 'type.value.code'));
    const [additionalInterestPartyTypeSelectedML, setAdditionalInterestPartyTypeSelectedML] = useState(_.get(additionalInterestVM, 'type.value.code') !== undefined);
    const [additionalInterestContactSubtypeSelectedML, setAdditionalInterestContactSubtypeSelectedML] = useState(_.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') !== undefined);
    const [additionalInterestSelectedML, setAdditionalInterestSelectedML] = useState(
        _.get(additionalInterestVM, 'publicID.value') !== undefined ||
        _.get(additionalInterestVM, 'policyAdditionalInterest.publicID.value') !== undefined);

    const interestVMRef = useRef(additionalInterestVM);
    const [isModernProduct, setIsModernProduct] = useState(false);

    const {
        clearBlockAlerts,
        addWarningAlert
    } = MLNotificationBlockUtil();

    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    useEffect(() => {
        if (!_.isUndefined(submissionVM)) {
            setIsModernProduct(_.get(submissionVM, 'isModernProduct_Ext.value', false));
        }

        if (!_.isUndefined(endorsementVM)) {
            setIsModernProduct(_.get(endorsementVM, 'isModernProduct_Ext.value', false));
        }

        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    /*
    * If the component is no longer visible,
    * set its validation to true to avoid isComponentValid from getting stuck with an invalid validation that no longer matters
   */
    useEffect(() => {
        return () => {
            if (onValidate) {
                onValidate(true, id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isContactReadonly = () => {
        return additionalInterestVM.policyAdditionalInterest
            && additionalInterestVM.policyAdditionalInterest.isFrequentMortgageeContact_ML
            && additionalInterestVM.policyAdditionalInterest.isFrequentMortgageeContact_ML.value;
    };

    const isInterestAlreadyPersisted = useCallback(() => {
        return !_.isUndefined(additionalInterestVM.value.fixedId);
    }, [additionalInterestVM.value.fixedId]);

    const isInterestPopulated = useCallback(() => {
        return additionalInterestVM.aspects.valid && additionalInterestVM.aspects.subtreeValid;
    }, [additionalInterestVM.aspects.subtreeValid, additionalInterestVM.aspects.valid]);

    const getCompanyDisplayName = useCallback((contact) => {
        if (contact.publicID === OTHER) {
            return translator(messages.other);
        }
        return `${_.startCase(_.toLower(contact.contactName ? contact.contactName : contact.displayName))}, ${contact.primaryAddress.city}, ${contact.primaryAddress.state}`;
    }, [translator]);

    const resetContactInfo = useCallback(() => {
        const contact = _.get(additionalInterestVM, 'value.policyAdditionalInterest');
        if (contact.firstName) _.set(contact, 'firstName', undefined);
        if (contact.lastName) _.set(contact, 'lastName', undefined);
        if (contact.middleName) _.set(contact, 'middleName', undefined);
        if (contact.suffix) _.set(contact, 'suffix', undefined);
        if (contact.contactName) _.set(contact, 'contactName', undefined);
        if (contact.addressBookUID_ML) _.set(contact, 'addressBookUID_ML', undefined);
        if (contact.isFrequentMortgageeContact_ML) _.set(contact, 'isFrequentMortgageeContact_ML', undefined);
    }, [additionalInterestVM]);

    const addExistingAdditionalInterestToPotentialContactsML = useCallback((newPotentialContacts) => {

        const currentAddltInterestContact = _.get(additionalInterestVM, 'policyAdditionalInterest.value');
        const existingPotentialContact = newPotentialContacts.find((contact) =>
            contact.displayName && currentAddltInterestContact.displayName &&
            contact.displayName === currentAddltInterestContact.displayName &&
            contact.addressBookUID_ML && currentAddltInterestContact.addressBookUID_ML &&
            contact.addressBookUID_ML === currentAddltInterestContact.addressBookUID_ML
        );
        if (existingPotentialContact) {
            currentAddltInterestContact.isFrequentMortgageeContact_ML = true;
            newPotentialContacts = newPotentialContacts.filter((contact) => contact.publicID !== existingPotentialContact.publicID);
        }

        if (!newPotentialContacts.some((potentialContact) => {
            return potentialContact.publicID && currentAddltInterestContact.publicID &&
                potentialContact.publicID === currentAddltInterestContact.publicID
        })) {
            newPotentialContacts.push(currentAddltInterestContact);
        }
        return [...newPotentialContacts];
    }, [additionalInterestVM]);

    useEffect(() => {
        let newPotentialContacts = Array.from(initialPotentialContact);

        if (!newPotentialContacts || !Array.isArray(newPotentialContacts)) {
            return;
        }

        if (newPotentialContacts.filter((c) => OTHER === c.publicID).length === 0) {
            const otherOption = {
                publicID: OTHER,
                contactName: null,
                primaryAddress: {
                    postalCode: null
                },
                skipPrivacyDetails_ML: true,
                accountHolder: false,
                subtype: CONTACT_SUBTYPE_COMPANY
            };
            newPotentialContacts.push(otherOption);

            newPotentialContacts = addExistingAdditionalInterestToPotentialContactsML(newPotentialContacts);

            // eslint-disable-next-line max-len
            // If the interest is already persisted but does not contain any UUID it means that the selection will default to OTHER
            // If this is the first time then we default to select
            if (!isContactReadonly() && isInterestAlreadyPersisted()
                // eslint-disable-next-line max-len
                && (additionalInterestVM.type.value && contactSelectionApplicableTypes.indexOf(additionalInterestVM.type.value.code) > -1)) {
                const origContact = _.clone(additionalInterestVM.policyAdditionalInterest.value);
                const otherContact = newPotentialContacts.find((c) => c.publicID === OTHER);
                if (_.isUndefined(additionalInterestVM.value.fixedId)) {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.value', otherContact);
                }
                additionalInterestVM.policyAdditionalInterest.contactName.value = origContact.contactName;
                additionalInterestVM.policyAdditionalInterest.primaryAddress.value = origContact.primaryAddress;
            }
            const formattedPotentialContacts = newPotentialContacts.map((contact) => {
                return {
                    code: contact.publicID,
                    name: getCompanyDisplayName(contact)
                };
            }).sort((a, b) => {
                if (a.name === messages.other.defaultMessage) {
                    return 1;
                }
                if (b.name === messages.other.defaultMessage) {
                    return -1;
                }
                return a.name.toUpperCase().localeCompare(b.name.toUpperCase());
            });
            updatePotentialContactsDropdown(formattedPotentialContacts);
            updateAvailablePotentialContacts(newPotentialContacts);
        } else {
            newPotentialContacts = addExistingAdditionalInterestToPotentialContactsML(newPotentialContacts);

            const formattedPotentialContacts = newPotentialContacts.map((contact) => {
                return {
                    code: contact.publicID,
                    name: getCompanyDisplayName(contact)
                };
            }).sort((a, b) => {
                if (a.name === messages.other.defaultMessage) {
                    return 1;
                }
                if (b.name === messages.other.defaultMessage) {
                    return -1;
                }
                return a.name.toUpperCase().localeCompare(b.name.toUpperCase());
            });
            updatePotentialContactsDropdown(formattedPotentialContacts);
            updateAvailablePotentialContacts(newPotentialContacts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPotentialContact]);

    useEffect(() => {
        const interestTypeCode = _.get(additionalInterestVM, 'type.value.code');
        if ((!(isInterestAlreadyPersisted() || isInterestPopulated()) && interestTypeCode !== prevInterestType)
            && !additionalInterestVM.value.ignoreEdit) {
            resetContactInfo();
            if (contactSelectionApplicableTypes.indexOf(interestTypeCode) > -1
                && !isInterestAlreadyPersisted()) {
                /**
                 * We must select a company from the contact selection dropdown, or
                 * provide our own company. Either way, we need to ensure
                 * the contact type is a company
                 */
                const contactSubtype = _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value', undefined);
                if (contactSubtype !== CONTACT_SUBTYPE_COMPANY) {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.subtype.value', CONTACT_SUBTYPE_COMPANY);
                }
            }

            if (defaultToSelectBusinessContactByTypes.indexOf(interestTypeCode) > -1) {
                _.set(additionalInterestVM, 'policyAdditionalInterest.subtype.value', CONTACT_SUBTYPE_COMPANY);
                setAdditionalInterestContactSubtypeSelectedML(true);
            }
            updateViewModel();
        }
        updatePrevInterestType(interestTypeCode)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalInterestVM.type.value]);

    useEffect(() => {
        const additionalInterestType = _.get(additionalInterestVM, 'type');
        let interestedPartyTypeTypeCodes = _.filter(
            additionalInterestType.aspects.availableValues,
            (typeCode) => {
                switch (typeCode.code) {
                    case FIRST_MORTGAGEE:
                        return !getSelectedInterestTypes().has(FIRST_MORTGAGEE)
                            || (!!additionalInterestType.value
                                && additionalInterestType.value.code === FIRST_MORTGAGEE);
                    case SECOND_MORTGAGEE:
                        return (getSelectedInterestTypes().has(FIRST_MORTGAGEE)
                            && !getSelectedInterestTypes().has(SECOND_MORTGAGEE))
                            || (!!additionalInterestType.value
                                && additionalInterestType.value.code === SECOND_MORTGAGEE);
                    case THIRD_MORTGAGEE:
                        return (getSelectedInterestTypes().has(SECOND_MORTGAGEE)
                            && !getSelectedInterestTypes().has(THIRD_MORTGAGEE))
                            || (!!additionalInterestType.value
                                && additionalInterestType.value.code === THIRD_MORTGAGEE);
                    default:
                        return true;
                }
            }
        )

        if (isModernProduct && !isHomeCondoRentersLL) {
            interestedPartyTypeTypeCodes = _.filter(interestedPartyTypeTypeCodes, (typeCode) => modernProductInterestTypes.includes(typeCode.code));

            const model = submissionVM ?? endorsementVM;

            if (!modernProductThirdPartyStates.includes(model?.baseData?.policyAddress?.state?.value?.code)) {
                interestedPartyTypeTypeCodes = _.filter(interestedPartyTypeTypeCodes, (typeCode) => !thirdPartyTypes.includes(typeCode.code));
            }
        } else {
            interestedPartyTypeTypeCodes = _.filter(interestedPartyTypeTypeCodes, (typeCode) =>
                !['LESSOR', 'additonal_vehicle_insured_np', 'certificate_of_insurance_np', 'additional_registered_owner_np'].includes(typeCode.code));
        }
        if(isLLHOrLLC) {
            interestedPartyTypeTypeCodes = _.filter(interestedPartyTypeTypeCodes, (typeCode) =>
                !['entity_Ext'].includes(typeCode.code));
        }
        if(isModernProduct && !isHome && !isCondo) {
            interestedPartyTypeTypeCodes = _.filter(interestedPartyTypeTypeCodes, (typeCode) =>
                !['other_payor_ml', 'additional_insured_slp_ml'].includes(typeCode.code));
        }

        const filteredTypeCodes = interestedPartyTypeTypeCodes.map((interestedPartyType) => ({
            code: interestedPartyType.code,
            name: translator({
                id: interestedPartyType.name,
                defaultMessage: interestedPartyType.name
            })
        }));

        updateInterestedPartyTypeCodes(filteredTypeCodes);
    }, [additionalInterestVM, getSelectedInterestTypes, isModernProduct, translator]);

    const getContactDetails = (item) => {
        if (item.policyAdditionalInterest.subtype === CONTACT_SUBTYPE_COMPANY) {
            return item.policyAdditionalInterest.contactName ? item.policyAdditionalInterest.contactName : undefined;
        }
        return item.policyAdditionalInterest.firstName && item.policyAdditionalInterest.lastName ?
            `${item.policyAdditionalInterest.firstName} ${item.policyAdditionalInterest.lastName}` : undefined;
    };

    useEffect(() => {
        const oldInterestVM = interestVMRef.current;
        if (additionalInterestVM.value === oldInterestVM.value) {
            return;
        }
        const contactDetailsNew = getContactDetails(additionalInterestVM.value);
        const newType = additionalInterestVM.type.value ? additionalInterestTypeList.getCode(additionalInterestVM.type.value.code) : undefined;
        const nameOfTypeNew = newType ? translator({
            id: newType.name,
            defaultMessage: newType.name
        }) : '';

        const contactDetailsOld = getContactDetails(oldInterestVM.value);
        const oldType = oldInterestVM.type.value ? additionalInterestTypeList.getCode(oldInterestVM.type.value.code) : undefined;
        const nameOfTypeOld = oldType ? translator({
            id: oldType.name,
            defaultMessage: oldType.name
        }) : '';

        if (MLNavigationStepsTrackingService.getData()) {
            if (additionalInterestVM.value.exists) { // if the interest was already filed it's an edit, otherwise it's an add
                MLNavigationStepsTrackingService.removeFromDynamicSteps(translator(messages.editItem, { item: contactDetailsOld ? `${nameOfTypeOld}` : `${nameOfTypeOld}` }), true, currentStepName, authHeader);

                if (contactDetailsNew && !_.isEqual(interestOriginalCopy, additionalInterestVM.value)) { // add only if the new value differs from the original
                    MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editItem, { item: contactDetailsNew ? `${nameOfTypeNew}` : `${nameOfTypeNew}` }), true, currentStepName, authHeader);
                }
            } else { // update the entry
                MLNavigationStepsTrackingService.removeFromDynamicSteps(translator(messages.addItem, { item: contactDetailsOld ? `${nameOfTypeOld}` : `${nameOfTypeOld}` }), true, currentStepName, authHeader);
                if (contactDetailsNew) {
                    MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addItem, { item: contactDetailsNew ? `${nameOfTypeNew}` : `${nameOfTypeNew}` }), true, currentStepName, authHeader);
                }
            }
        }
        updateViewModel();
        interestVMRef.current = additionalInterestVM;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalInterestVM.value])

    const hasType = useCallback(() => {
        const type = _.get(additionalInterestVM, 'type');
        if (type && type.value && type.value.code) {
            return true;
        }
        return false;
    }, [additionalInterestVM]);
    
    const shouldShowPropertyDescription = useCallback(() => {
        const typeCode = _.get(additionalInterestVM, 'type.value.code');
        return hasType() && typeCode === 'additional_insured_slp_ml';
    }, [additionalInterestVM, hasType]);

    const getPropertyContent = useCallback(() => {
         if (isHomeCondo && shouldShowPropertyDescription()) {
             return translator(messages.describedProperty);
         }
         return translator(messages.propertyDescription); 
     }, [isHomeCondo]);

    const shouldShowCovOptions = useCallback(() => {
    const typeCode = _.get(additionalInterestVM, 'type.value.code');

    return hasType() && (
        (isHomeCondo && typeCode === 'additional_insured_llc_ml') ||
        (isHomeCondoRentersLL && typeCode === 'ADDITIONALINSURED_ML')
    );
}, [additionalInterestVM, hasType, isHomeCondo, isHomeCondoRentersLL]);

    const shouldShowContactSelection = useCallback(() => {
        const typeCode = _.get(additionalInterestVM, 'type.value.code');
        return hasType() && contactSelectionApplicableTypes.indexOf(typeCode) > -1;
    }, [additionalInterestVM, hasType]);

    const shouldShowContactDetails = useCallback(() => {
        const contactPublicID = _.get(additionalInterestVM, 'policyAdditionalInterest.value.publicID');
        const typeCode = _.get(additionalInterestVM, 'type.value.code');
        if (shouldShowContactSelection()) {
            return !!contactPublicID || (hasType() && leinOrLeasingCompanyTypes.indexOf(typeCode) > -1);
        }
        return true;
    }, [additionalInterestVM, shouldShowContactSelection]);

    const drivers = useCallback(() => {
        if (!_.isUndefined(submissionVM)) {
            return _.get(submissionVM, 'lobData.personalAuto.coverables.drivers.value');
        }
        if (!_.isUndefined(endorsementVM)) {
            return _.get(endorsementVM, 'lobData.personalAuto.coverables.drivers.value');
        }
        if (!_.isUndefined(additionalInterestVM)) {
            return _.get(additionalInterestVM, 'baseVMNode.container.lobData.personalUmbrella.coverables.vehicleOperators.value');
        }
        return undefined;
    }, [additionalInterestVM, endorsementVM, submissionVM]);

    const isExcludedDriver = useCallback(() => {
        if (!drivers()) {
            return undefined;
        }
        const excludedDriver = drivers().some((driver) => {
            if (!_.isUndefined(driver.type_ML))
                return driver.type_ML === 'excluded'
            return driver.driverType === 'excluded'
        });
        return excludedDriver;
    }, [drivers]);

    const shouldShowInterestPartyEmailSubmission = useCallback(() => {
        return !_.isNil(submissionVM)
            && additionalInterestVM.type.value
            && !_.isUndefined(submissionVM.value.isPersonalAuto())
            && ((submissionVM.baseData.policyAddress.state.value.code === 'RI' && additionalInterestVM.type.value.code === 'ADDITIONALINSURED_ML'
                && (submissionVM.quoteData.offeredQuotes.value[0].commPreference_ML === 'electronic' ||
                    submissionVM.quoteData.offeredQuotes.value[0].commPreference_ML === 'email'))
                || (isExcludedDriver() && (submissionVM.baseData.policyAddress.state.value.code === 'OR') && (additionalInterestVM.type.value.code === 'ADDITIONALINSURED_ML' || additionalInterestVM.type.value.code === 'joint_ownership_ml')));

    }, [additionalInterestVM, isExcludedDriver, submissionVM]);

    const shouldShowInterestedPartyEmailEndorsement = useCallback(() => {
        return !_.isNil(endorsementVM)
            && additionalInterestVM.type.value
            && endorsementVM.baseData.productCode.value === 'PersonalAuto'
            && ((endorsementVM.baseData.policyAddress.state.value.code === 'RI'
                && additionalInterestVM.type.value.code === 'ADDITIONALINSURED_ML'
                && (endorsementVM.baseData.policyBillingDocCommPref_ML.value.code === 'electronic' ||
                    endorsementVM.baseData.alertsStatusUpdatesCommPref_ML.value.code === 'email'))
                || (isExcludedDriver() && (endorsementVM.baseData.policyAddress.state.value.code === 'OR') && (additionalInterestVM.type.value.code === 'ADDITIONALINSURED_ML' || additionalInterestVM.type.value.code === 'joint_ownership_ml')));
    }, [additionalInterestVM, endorsementVM, isExcludedDriver]);

    const shouldShowInterestedPartyEmailAdditionalInterest = useCallback(() => {
        return !_.isNil(additionalInterestVM)
            && !_.isUndefined(additionalInterestVM.type.value)
            && additionalInterestVM.type.value.code === 'ADDITIONALINSURED_ML'
            && isExcludedDriver()
            && (_.get(additionalInterestVM, 'type.baseVMNode.container.lobData.personalUmbrella.coverables.vehicleOperators.value[0].contact.address.state', '') === 'OR');
    }, [additionalInterestVM, isExcludedDriver]);

    const isThirdParty = useCallback(() => {
        return hasType() && thirdPartyTypes.includes(additionalInterestVM.type.value.code);
    }, [additionalInterestVM.type, hasType]);


    const shouldShowInterestedPartyEmail = useCallback(() => {
        return (shouldShowInterestPartyEmailSubmission() || shouldShowInterestedPartyEmailEndorsement() || shouldShowInterestedPartyEmailAdditionalInterest()) &&
            !isThirdParty();
    }, [isThirdParty, shouldShowInterestPartyEmailSubmission, shouldShowInterestedPartyEmailEndorsement, shouldShowInterestedPartyEmailAdditionalInterest]);

    useEffect(() => {
        setShowContactDetails(shouldShowContactDetails());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveAdditionalInterest = useCallback(() => {
        removeAdditionalInterest(index);
    }, [index, removeAdditionalInterest]);

    const setAdditionalInterestML = useCallback((additionalInterestAccountContactValue, resetContactSubtype) => {
        const newInterestInitialVal = {
            type: additionalInterestVM.type.value.code,
            addlInsCovOptions:additionalInterestVM?.addlInsCovOptions?.value?.code,
            leaseOrLoanNumber_Ext: additionalInterestVM?.leaseOrLoanNumber_Ext?.value,
            description: additionalInterestVM?.description?.value,
            subtype: subtype,
            publicID: OTHER,
            policyAdditionalInterest: additionalInterestAccountContactValue || {
                primaryAddress: {}
            }
        };
        newInterestInitialVal.policyAdditionalInterest.accountHolder = false;
        newInterestInitialVal.policyAdditionalInterest.skipPrivacyDetails_ML = true;

        newInterestInitialVal.policyAdditionalInterest.subtype = resetContactSubtype ? undefined :
            _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value');

        const additionalInterestDTO = viewModelService.create(newInterestInitialVal, 'pc', 'edge.capabilities.policyjob.lob.AdditionalInterestDTO');
        additionalInterestVM.value = additionalInterestDTO.value;
        updateViewModel();
    }, [additionalInterestVM, subtype, updateViewModel, viewModelService]);

    const updateContactSubtype = (value) => {
        setAdditionalInterestML();
        _.set(additionalInterestVM, 'policyAdditionalInterest.subtype', value);
        setAdditionalInterestContactSubtypeSelectedML(true);
        //US934397 Interested Party fields 
        setShowContactDetails(true)
        setAdditionalInterestSelectedML(true);

        updateViewModel();
    };

    const handlePolicyBillingDocCommPref = (value) => {
        _.set(additionalInterestVM, 'policyAdditionalInterest.policyBillingDocCommPref_Ext', value);
        updateViewModel();
    };

    const setContactSelectedML = useCallback(() => {
        setAdditionalInterestSelectedML(true);
    }, []);

    const handleContactSelectionChange = (value) => {
        setAdditionalInterestML();
        const selectedContact = _.find(availablePotentialContacts, (contact) => {
            return contact.publicID === value;
        });
        //US1129678 Unable to continue from Additional details screen after adding Third mortgage
        if(shouldShowContactDetails() == false){
            if(additionalInterestVM?.value?.policyAdditionalInterest?.policyBillingDocCommPref_Ext !== undefined
                && additionalInterestVM?.value?.policyAdditionalInterest?.policyBillingDocCommPref_Ext !== 'paper'){
                additionalInterestVM.value.policyAdditionalInterest.policyBillingDocCommPref_Ext = undefined;
            }
        }
        _.set(additionalInterestVM, 'policyAdditionalInterest.value', selectedContact);
        if (!OTHER === selectedContact.publicID) {
            _.set(additionalInterestVM, 'policyAdditionalInterest.skipPrivacyDetails_ML.value', true);
        }
        updateViewModel();
        setContactSelectedML();
        setShowContactDetails(shouldShowContactDetails());
        setTimeout(() => {
            refreshData();
        }, 100);
    };

    const shouldShowLoanNumber = useCallback(() => {
        const loanNumberApplicableTypes = [
            'FIRSTMORTGAGEE',
            'SECONDMORTGAGEE',
            'THIRDMORTGAGEE',
            'LIEN',
            'LEASINGCOMPANY',
            'LESSOR'
        ];
        const typeCode = _.get(additionalInterestVM, 'type.value.code');
        return hasType()
            && loanNumberApplicableTypes.indexOf(typeCode) > -1;
    }, [additionalInterestVM, hasType]);

    const getTypeName = useCallback(() => {
        /**
         * For frequent mortgagee + lienholder etc selection, the dropdown label is
         * "Select your XX Company" where XX is the right hand side of the below map
         * for the following types.
         * It is also used as the company name input label in the form of:
         * "XX Company Name", where XX is again the right hand side of the below map.
         */
        const interestTypeDisplayMap = {
            FIRSTMORTGAGEE: messages.mortgageCompany,
            SECONDMORTGAGEE: messages.mortgageCompany,
            THIRDMORTGAGEE: messages.mortgageCompany,
            LEASINGCOMPANY: messages.leasingCompany,
            LIEN: messages.lienholderCompany,
            LESSOR: messages.lessorCompany,
            additional_insured_slp_ml: messages.additional_insured_slp_ml
        };

        const interestPartyType = _.get(additionalInterestVM, 'type.value');
        if (interestPartyType) {
            return translator(interestTypeDisplayMap[interestPartyType.code]) || translator({
                id: interestPartyType.name,
                defaultMessage: interestPartyType.name
            });
        }
        return '';
    }, [additionalInterestVM, translator]);

    const handleInterestedPartyChange = useCallback((value) => {
        const selectedType = additionalInterestTypeList.getCode(value);
        _.set(additionalInterestVM, 'type', selectedType);

        //US934397 Interested Party fields 
        setShowContactDetails(true)
        setAdditionalInterestSelectedML(true)

        setAdditionalInterestPartyTypeSelectedML(true);
        setAdditionalInterestContactSubtypeSelectedML(false);
        setAdditionalInterestML(undefined, true);

        updateViewModel();
        if (isThirdParty()) {
            addWarningAlert(messages.thirdPartyAlertMessage, 'thirdPartyAlert');
            window.scrollTo(0, 0);
        } else {    
            clearBlockAlerts('thirdPartyAlert');
        }
    }, [addWarningAlert, additionalInterestTypeList, additionalInterestVM, clearBlockAlerts, isThirdParty, setAdditionalInterestML, updateViewModel]);

    const writeValue = useCallback((value, changedPath) => {
        onValueChange(value, `${path}.${changedPath}`);
    }, [onValueChange, path]);

    const hasDefaultSetRef = useRef(false);
 
    useEffect(() => {
        if (!isQnB) {
            return
        }
        if (!defaultInterestPartytype) {
            hasDefaultSetRef.current = false;
            return;
        }

        const currentTypeCode = _.get( additionalInterestVM, 'type.value.code');
 
        if (
            !hasDefaultSetRef.current && currentTypeCode !== defaultInterestPartytype
        ) {
            hasDefaultSetRef.current = true;
            handleInterestedPartyChange(defaultInterestPartytype);
        }
    }, [defaultInterestPartytype, additionalInterestVM, handleInterestedPartyChange ]);

    const getAdditionalInterestAvailableCandidatesByContactSubtypeML = useCallback(() => {
        const contactSubtype = _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value');
        return additionalInterestAvailableCandidatesML.filter((candidate) =>
            candidate.subtype === contactSubtype);
    }, [additionalInterestAvailableCandidatesML, additionalInterestVM]);

    const getInitPublicIDML = useCallback(() => {
        const publicID = _.get(additionalInterestVM, 'policyAdditionalInterest.publicID.value');
        return publicID || _.get(additionalInterestVM, 'publicID.value') ? 'new' : undefined;
    }, [additionalInterestVM]);

    const getInterestPartySubtypeValue = useCallback(() => {
        if (_.get(additionalInterestVM, 'type.value.code') === 'TRUST_ML') {
            return _.get(additionalInterestVM.value, 'policyAdditionalInterest.subtype') ? _.get(additionalInterestVM.value, 'policyAdditionalInterest.subtype') : CONTACT_SUBTYPE_COMPANY;
        } else {
            return _.get(additionalInterestVM.value, 'policyAdditionalInterest.subtype');
        }
    }, [additionalInterestVM]);

    const showCompanyName = useCallback(() => {
        if (_.get(additionalInterestVM, 'type.value.code') === 'TRUST_ML') {
            return !(_.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') === PERSON);
        } else {
            _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') === COMPANY;
        }
    }, [additionalInterestVM]);

    const validatePrimaryEmail = () => {
        let emailAddress1 =  _.get(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value');
        if(emailAddress1){
            emailAddress1=emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = isQnB ? EmailValidationService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            ) : AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification Api Called");
                if (res == true) {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', true);
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value', emailAddress1);
                }
                else {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', false);
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value', emailAddress1 + " ");
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(messages.invalidEmail), 'invalidEmail');
                _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', false);
                _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value', emailAddress1);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
            });
        }
    }

    const primaryEmailTrim = () => {
        let currEmail = _.get(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value');
        let isCurrEmailVerified = _.get(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext.value');
        if(currEmail !== '' && currEmail !== undefined && isCurrEmailVerified){
            currEmail = currEmail.trim();
            _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress1.value', currEmail);
        }
    }

    const validateSecondaryEmail = () => {
        let emailAddress1 =  _.get(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value');
        if(emailAddress1){
            emailAddress1=emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = isQnB ? EmailValidationService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            ) : AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification Api Called");
                if (res == true) {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', true);
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value', emailAddress1);
                }
                else {
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', false);
                    _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value', emailAddress1 + " ");
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(messages.invalidEmail), 'invalidEmail');
                _.set(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext', false);
                _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value', emailAddress1);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
            });
        }
    }

    const SecondaryEmailTrim = () => {
        let currEmail = _.get(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value');
        let isCurrEmailVerified = _.get(additionalInterestVM, 'policyAdditionalInterest.emailVerified_Ext.value');
        if(currEmail !== '' && currEmail !== undefined && isCurrEmailVerified){
            currEmail = currEmail.trim();
            _.set(additionalInterestVM, 'policyAdditionalInterest.emailAddress2_ML.value', currEmail);
        }
    }


    const resolvers = {
        resolveCallbackMap: {
            onRemoveAdditionalInterest: handleRemoveAdditionalInterest,
            updateContactSubtype,
            onHandleInterestedPartyChange: handleInterestedPartyChange,
            onHandlePolicyBillingDocCommPref: handlePolicyBillingDocCommPref,
            onContactSelectionChange: handleContactSelectionChange
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mlAddressDetailsComponent: MLAddressDetailsComponent,
            mlNameDetailsComponent: MLNameDetailsComponent,
            mlPolicyNamedInsuredCandidatesComponent: MLPolicyNamedInsuredCandidatesComponent
        }
    };

    const overrideProps = {
        interestedParty: {
            content: translator(messages.interestedParty, { number: (index) }),
            visible: display
        },
        interestedPartyContainer: {
            visible: displayInterestedPartyTypeSelection
        },
        interestedPartyTypeDropDown: {
            availableValues: interestedPartyTypeCodes
        },
        formFieldsContainer: {
            visible: additionalInterestPartyTypeSelectedML
        },
        propertyDescriptionContainer: {
            visible: shouldShowPropertyDescription()
        },
        interestedPartyCovContainer:{
            visible:  isHomeCondoRentersLL && shouldShowCovOptions()
        },
        leaseOrLoanNumberContainer: {
            visible: isHomeCondo && shouldShowPropertyDescription()
        },
        propertyDescription:{
            label: getPropertyContent()
        },
        interestPartySubtype: {
            id: `interestPartySubtype${index}`,
            visible: (_.get(additionalInterestVM, 'type.value.code') === 'TRUST_ML' && _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') === PERSON) || (!(_.get(additionalInterestVM, 'type.value.code') === 'TRUST_ML') && ([undefined, OTHER].includes(_.get(additionalInterestVM, 'publicID.value')) && hasType() && !shouldShowContactSelection())),
            label: translator(messages.typeOfInterestedPartyType, { interestedPartyType: getTypeName() }),
            value: getInterestPartySubtypeValue(),
            path: 'policyAdditionalInterest.subtype',
            className: isQnB ? "row col-md-12 vertical-spacing-10 radioButtonQnB" : "row col-md-12 vertical-spacing-10"
        },
        additionalInterestCandidatesContainer: {
            visible: [undefined, OTHER].includes(_.get(additionalInterestVM, 'publicID.value')) && !shouldShowContactSelection() && additionalInterestContactSubtypeSelectedML
        },
        additionalInterestCandidatesComponent: {
            submissionVM,
            initPublicID: getInitPublicIDML(),
            setNamedInsured: setAdditionalInterestML,
            namedInsuredCandidates: getAdditionalInterestAvailableCandidatesByContactSubtypeML(),
            label: {
                id: 'common.views.additional-interest-input.Add Additional Interest',
                defaultMessage: 'Add Additional Interest'
            },
            setContactSelected: setContactSelectedML,
            availableContactSelected: additionalInterestSelectedML,
            isNotfromAdditionalIntrestPage: false
        },
        personNameContainer: {
            visible: _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') === PERSON,
            onModelChange: updateViewModel
        },
        companyNameContainer: {
            visible: _.get(additionalInterestVM, 'policyAdditionalInterest.subtype.value') === COMPANY,
            onModelChange: updateViewModel
        },
        companyName: {
            visible: showCompanyName(),
            label: translator(messages.companyNameLabel, { interestedPartyType: getTypeName() }),
            disabled: isContactReadonly(),
            className: isContactReadonly() ? "mlReadOnlyFieldComponent vertical-spacing-35" : "ml-vertical-spacing-50",
            value: _.get(additionalInterestVM.value, 'policyAdditionalInterest.contactName')
        },
        contactDetailsContainer: {
            visible: showContactDetails && additionalInterestSelectedML
        },
        loanContractNumberContainer: {
            visible: shouldShowLoanNumber()
        },
        addressContainer: {
            visible: !_.isEmpty(additionalInterestVM.policyAdditionalInterest.subtype.value)
        },
        addressComponent: {
            path: `${path}.policyAdditionalInterest.primaryAddress`,
            addressVM: _.get(additionalInterestVM, 'policyAdditionalInterest.primaryAddress'),
            initialLockStateCity: true,
            showStateCode: true,
            onValidate,
            showValidationErrors,
            onModelChange: updateViewModel,
            layout: 'ml-policy-change',
            addressLine2PlaceHolder: messages.aptUnitLabel,
            readOnly: isContactReadonly()
        },
        selectPotentialContact: {
            label: translator(messages.selectYour, { interestedPartyType: getTypeName() }),
            availableValues: potentialContactsDropdown,
            visible: shouldShowContactSelection(),
            value: _.get(additionalInterestVM, 'policyAdditionalInterest.value.publicID'),
            required: true
        },
        contactPreferences: {
            visible: isThirdParty()
        },
        mlNameDetailsComponent: {
            personVM: _.get(additionalInterestVM, 'policyAdditionalInterest'),
            onValidate: onValidate,
            showValidationErrors,
            onModelChange: writeValue
        },
        removeAdditionalInterest: {
            visible: display
        },
        interestedPartyEmailContainer: {
            visible: shouldShowInterestedPartyEmail(),
            onBlur: validatePrimaryEmail,
            onFocus: primaryEmailTrim
        },
        primaryEmail: {
            onBlur: validatePrimaryEmail,
            onFocus: primaryEmailTrim
        },
        secondaryEmail: {
            onBlur: validateSecondaryEmail,
            onFocus: SecondaryEmailTrim
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            overrideProps={overrideProps}
            model={additionalInterestVM}
            onValidationChange={setComponentValidation}
            onValueChange={writeValue}
            showErrors={showValidationErrors}
        />
    );
}

AdditionalInterestInputComponent.propTypes = {
    addtlInterestVM: PropTypes.shape({}).isRequired,
    addressVM: PropTypes.shape({}),
    id: PropTypes.string.isRequired,
    removeAdditionalInterest: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    path: PropTypes.string.isRequired,
    onValueChange: PropTypes.func.isRequired,
    updateViewModel: PropTypes.func.isRequired,
    getSelectedInterestTypes: PropTypes.func.isRequired,
    initialPotentialContact: PropTypes.shape([]).isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired,
    displayInterestedPartyTypeSelection: PropTypes.bool,
    display: PropTypes.bool,
    currentStepName: PropTypes.string,
    submissionVM: PropTypes.shape({}),
    endorsementVM: PropTypes.shape({}),
};

AdditionalInterestInputComponent.defaultProps = {
    displayInterestedPartyTypeSelection: true,
    display: true,
    submissionVM: undefined,
    endorsementVM: undefined,
    additionalInterestAvailableCandidatesML: []
};

export default AdditionalInterestInputComponent;
